import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  isTransientPlacesStatus,
  loadPlacesLibrary,
  resetGoogleMapsLoader,
} from "shared/utils/googleMaps";
import OptionsDropDown from "../options";
import styles from "./style.module.scss";

const MAX_STATUS_RETRIES = 2;
const STATUS_RETRY_BASE_MS = 400;

interface InputProps extends React.HTMLProps<HTMLInputElement> {
  label: string;
  error?: string;
  notRequiredMargin?: boolean;
  customLabelStyle?: any;
  customInputStyle?: any;
  customInputContainer?: any;
  customIconStyle?: any;
  Icon?: any;
  formikKey: string;
  setFieldValue: (val: any, val2: any) => void;
  readOnlyColor?: string;
  addressKey?: string;
  value: string;
}

const CustomAutoComplete = ({
  label,
  required,
  error,
  value,
  placeholder,
  notRequiredMargin,
  onKeyDown,
  customLabelStyle,
  customInputStyle,
  customInputContainer,
  customIconStyle,
  readOnly,
  Icon,
  onPaste,
  defaultValue,
  formikKey,
  setFieldValue,
  readOnlyColor,
  id,
  addressKey,
}: InputProps) => {
  const [search, setSearch] = useState<string>(value || "");
  const [options, setOptions] = useState<any[]>([]);
  const [openProfileDropDown, setOpenDropDown] = useState<boolean>(false);
  // Start false so a prefilled `value` (e.g. a saved address on the settings
  // page) is treated as an already-selected address and does NOT trigger a
  // prediction fetch / auto-open on mount. Typing sets this true (onChange).
  const [showOptions, setShowOption] = useState<boolean>(false);
  const [lookupUnavailable, setLookupUnavailable] = useState<boolean>(false);
  const [placesReady, setPlacesReady] = useState<boolean>(false);
  const [loadAttempt, setLoadAttempt] = useState<number>(0);
  const placesRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;

    loadPlacesLibrary()
      .then((places) => {
        if (cancelled) return;
        if (!places?.AutocompleteService) {
          setLookupUnavailable(true);
          return;
        }
        placesRef.current = places;
        setLookupUnavailable(false);
        setPlacesReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Google Maps address lookup unavailable:", error);
        setLookupUnavailable(true);
      });

    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const retryLookup = useCallback(() => {
    resetGoogleMapsLoader();
    placesRef.current = null;
    setPlacesReady(false);
    setLookupUnavailable(false);
    setLoadAttempt((attempt) => attempt + 1);
  }, []);

  // Coming back online is the one signal worth reconnecting on automatically.
  useEffect(() => {
    if (!lookupUnavailable) return;
    window.addEventListener("online", retryLookup);
    return () => window.removeEventListener("online", retryLookup);
  }, [lookupUnavailable, retryLookup]);

  useEffect(() => {
    if (!showOptions || !search) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const runPredictions = (statusRetries = 0) => {
      if (cancelled) return;
      const places = placesRef.current;
      if (!places?.AutocompleteService) return;

      const autocompleteService = new places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        {
          input: search,
          componentRestrictions: { country: ["us"] },
        },
        (predictions: any, status: string) => {
          if (cancelled) return;
          if (status && isTransientPlacesStatus(status)) {
            if (statusRetries < MAX_STATUS_RETRIES) {
              retryTimer = setTimeout(
                () => runPredictions(statusRetries + 1),
                STATUS_RETRY_BASE_MS * 2 ** statusRetries,
              );
              return;
            }
            setOpenDropDown(false);
            return;
          }
          if (status && status !== "OK" && status !== "ZERO_RESULTS") {
            console.warn("Google Places lookup failed with status:", status);
            setLookupUnavailable(true);
            setOpenDropDown(false);
            return;
          }
          if (predictions) {
            const arr = predictions.map((prediction: any) => ({
              title: prediction.description,
              Icon: null,
              action: () => {
                const selectedAddress = prediction.description;

                setSearch(selectedAddress);
                setFieldValue(formikKey, selectedAddress);

                if (addressKey) {
                  const geocoder = new (window as any).google.maps.Geocoder();
                  geocoder
                    .geocode({ placeId: prediction.place_id })
                    .then(({ results }: any) => {
                      const latitude =
                        results[0].geometry.location.lat() ?? null;
                      const longitude =
                        results[0].geometry.location.lng() ?? null;

                      setFieldValue(addressKey, [longitude, latitude]);
                    })
                    .catch((error: any) => {
                      console.error("Failed to get lat/long data:", error);
                    });
                }

                setShowOption(false);
                setOpenDropDown(false);
              },
            }));
            setOptions(arr);
            if (!openProfileDropDown) {
              setOpenDropDown(true);
            }
          } else {
            setOpenDropDown(false);
          }
        },
      );
    };

    const debounceTimer = setTimeout(runPredictions, 350);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, placesReady]);

  return (
    <div
      className={classNames(
        "relative w-full",
        notRequiredMargin ? "" : error ? "mb-0" : "mb-3",
      )}
    >
      <div className="flex flex-col items-start w-full gap-2">
        {label && (
          <label
            className={classNames(
              styles.inputLabel,
              customLabelStyle && customLabelStyle,
            )}
          >
            {label} {!!required && <span className={styles.asterik}>*</span>}
          </label>
        )}

        <div
          className={classNames(
            styles.inputContainer,
            customInputContainer && customInputContainer,
            "w-full",
          )}
        >
          {Icon && (
            <Icon className={classNames(customIconStyle, styles.iconStyle)} />
          )}
          <input
            id={id}
            value={search}
            onChange={(e) => {
              const inputValue = e.target.value;
              setSearch(inputValue);
              setShowOption(true);

              if (!inputValue) {
                setOpenDropDown(false);
                setFieldValue(formikKey, "");
              }
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={classNames(
              styles.inputStyle,
              customInputStyle && customInputStyle,
            )}
            readOnly={readOnly}
            onPaste={onPaste}
          />
        </div>
      </div>
      <OptionsDropDown
        options={options}
        openSelection={openProfileDropDown}
        setOpenSelection={setOpenDropDown}
        customContainer={styles.optionsContainer}
        wrapInGlobalWrapper={false}
      />
      {lookupUnavailable && (
        <div className={classNames(styles.lookupWarning)}>
          <span>Address lookup unavailable — type the address manually.</span>
          <button type="button" onClick={retryLookup}>
            Retry
          </button>
        </div>
      )}
      {!!error && <div className={classNames(styles.error)}>{error}</div>}
    </div>
  );
};

export default CustomAutoComplete;
